import { LEVEL_COUNTS } from '../lib/schoolDirectory';
import React, { useState, useMemo } from 'react';
import {
  GraduationCap, Building2, BookOpen, Users, MapPin, Phone, Mail, Globe,
  ChevronRight, ChevronDown, ChevronUp, Search, Filter, X,
  DollarSign, Clock, Calendar, Heart, Star, ExternalLink, Info, Award,
  Briefcase, Home, RefreshCw, Lightbulb, CheckCircle, AlertCircle,
  Target, TrendingUp, Zap
} from 'lucide-react';

// Import tertiary institutions data
import tertiaryData from '../data/tertiary-institutions.json';
import { BackButton, ResourceLinkGrid, ShowMoreList, DirectoryLink } from './shared';

// Format currency in JMD
const formatCurrency = (amount) => {
  if (!amount) return 'Contact school';
  return new Intl.NumberFormat('en-JM', { style: 'currency', currency: 'JMD', maximumFractionDigits: 0 }).format(amount);
};

// ==================== INSTITUTION CARD ====================
const InstitutionCard = ({ institution, onSelect }) => {
  const typeLabels = {
    university: { label: 'University', color: 'bg-purple-100 text-purple-700' },
    teachers_college: { label: 'Teachers College', color: 'bg-blue-100 text-blue-700' },
    community_college: { label: 'Community College', color: 'bg-brand-100 text-brand-700' },
    vocational: { label: 'Vocational/TVET', color: 'bg-orange-100 text-orange-700' },
    specialized: { label: 'Specialized', color: 'bg-pink-100 text-pink-700' },
    online: { label: 'Online/Distance', color: 'bg-cyan-100 text-cyan-700' }
  };

  const typeInfo = typeLabels[institution.institution_type] || { label: institution.institution_type, color: 'bg-gray-100 text-gray-700' };

  return (
    <div
      onClick={() => onSelect(institution)}
      className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-lg hover:border-brand-200 transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="font-bold text-gray-800 text-lg">{institution.short_name || institution.institution_name}</h3>
          <p className="text-sm text-gray-500">{institution.institution_name}</p>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${typeInfo.color}`}>
          {typeInfo.label}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 mb-3">
        <span className="flex items-center gap-1">
          <MapPin className="w-4 h-4" />
          {institution.parish}
        </span>
        <span className={`px-2 py-0.5 rounded text-xs ${institution.sector === 'Public' ? 'bg-brand-100 text-brand-700' : 'bg-amber-100 text-amber-700'}`}>
          {institution.sector}
        </span>
        {institution.ucj_registered && (
          <span className="flex items-center gap-1 text-brand-600 text-xs">
            <CheckCircle className="w-3 h-3" /> UCJ Registered
          </span>
        )}
      </div>

      {/* Key features */}
      <div className="flex flex-wrap gap-2 mb-3">
        {institution.has_evening_programmes && (
          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">Evening Classes</span>
        )}
        {institution.has_weekend_programmes && (
          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">Weekend Classes</span>
        )}
        {institution.has_online_programmes && (
          <span className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded">Online Options</span>
        )}
        {institution.mature_student_entry && (
          <span className="text-xs bg-brand-50 text-brand-700 px-2 py-1 rounded">Mature Entry</span>
        )}
        {institution.accepts_slb_loans && (
          <span className="text-xs bg-brand-50 text-brand-700 px-2 py-1 rounded">SLB Accepted</span>
        )}
      </div>

      {/* Tuition range */}
      {institution.tuition_range_undergraduate_jmd && (
        <div className="text-sm text-gray-600">
          <DollarSign className="w-4 h-4 inline" />
          <span className="font-medium">
            {formatCurrency(institution.tuition_range_undergraduate_jmd.min)} - {formatCurrency(institution.tuition_range_undergraduate_jmd.max)}
          </span>
          <span className="text-gray-400"> /year</span>
        </div>
      )}

      <p className="text-sm text-gray-500 mt-3 line-clamp-2">{institution.description}</p>

      <div className="flex items-center justify-end mt-3 text-brand-600 text-sm font-medium">
        View Details <ChevronRight className="w-4 h-4" />
      </div>
    </div>
  );
};

// ==================== INSTITUTION DETAIL MODAL ====================
const InstitutionDetail = ({ institution, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!institution) return null;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'programmes', label: 'Programmes' },
    { id: 'admissions', label: 'Admissions' },
    { id: 'costs', label: 'Costs & Aid' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full my-8 shadow-xl">
        {/* Header */}
        <div className="bg-gradient-to-br from-door-tertiary to-door-tertiary-dark rounded-t-2xl p-6 text-white relative">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full">
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 text-purple-200 text-sm mb-2">
            <span className={`px-2 py-0.5 rounded text-xs ${institution.sector === 'Public' ? 'bg-brand-500/20 text-brand-200' : 'bg-amber-500/20 text-amber-200'}`}>
              {institution.sector}
            </span>
            {institution.ucj_registered && (
              <span className="flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> UCJ Registered
              </span>
            )}
          </div>
          <h2 className="text-2xl font-black mb-1">{institution.institution_name}</h2>
          <div className="flex items-center gap-4 text-purple-100 text-sm">
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {institution.address || institution.parish}</span>
            {institution.founded_year && <span>Est. {institution.founded_year}</span>}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6 flex gap-4 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <p className="text-gray-600">{institution.description}</p>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {institution.student_population && (
                  <div className="bg-gray-50 rounded-xl p-4 text-center">
                    <Users className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                    <div className="text-xl font-bold text-gray-800">{institution.student_population.toLocaleString()}</div>
                    <div className="text-xs text-gray-500">Students</div>
                  </div>
                )}
                {institution.faculties_schools && (
                  <div className="bg-gray-50 rounded-xl p-4 text-center">
                    <Building2 className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                    <div className="text-xl font-bold text-gray-800">{institution.faculties_schools.length}</div>
                    <div className="text-xs text-gray-500">Faculties/Schools</div>
                  </div>
                )}
                {institution.degrees_offered && (
                  <div className="bg-gray-50 rounded-xl p-4 text-center">
                    <GraduationCap className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                    <div className="text-xl font-bold text-gray-800">{institution.degrees_offered.length}</div>
                    <div className="text-xs text-gray-500">Degree Types</div>
                  </div>
                )}
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <Clock className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                  <div className="text-sm font-bold text-gray-800">
                    {[institution.has_evening_programmes && 'Eve', institution.has_weekend_programmes && 'Wknd', institution.has_online_programmes && 'Online'].filter(Boolean).join(', ') || 'Day'}
                  </div>
                  <div className="text-xs text-gray-500">Schedule Options</div>
                </div>
              </div>

              {/* Faculties/Schools */}
              {institution.faculties_schools && (
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3">Faculties & Schools</h3>
                  <div className="flex flex-wrap gap-2">
                    {institution.faculties_schools.map((faculty, idx) => (
                      <span key={idx} className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-sm">
                        {faculty}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-800 mb-3">Contact Information</h3>
                <div className="space-y-2 text-sm">
                  {institution.contact_phone && (
                    <a href={`tel:${institution.contact_phone}`} className="flex items-center gap-2 text-gray-600 hover:text-brand-600">
                      <Phone className="w-4 h-4" /> {institution.contact_phone}
                    </a>
                  )}
                  {institution.email && (
                    <a href={`mailto:${institution.email}`} className="flex items-center gap-2 text-gray-600 hover:text-brand-600">
                      <Mail className="w-4 h-4" /> {institution.email}
                    </a>
                  )}
                  {institution.website && (
                    <a href={institution.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-brand-600 hover:underline">
                      <Globe className="w-4 h-4" /> Visit Website <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'programmes' && (
            <div className="space-y-4">
              {institution.notable_programmes && institution.notable_programmes.length > 0 ? (
                <>
                  <p className="text-sm text-gray-500 mb-4">Popular programmes offered at {institution.short_name || institution.institution_name}</p>
                  {institution.notable_programmes.map((prog, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-xl p-4">
                      <h4 className="font-semibold text-gray-800">{prog.name}</h4>
                      <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-600">
                        {prog.duration && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" /> {prog.duration}
                          </span>
                        )}
                        {prog.entry && (
                          <span className="flex items-center gap-1">
                            <BookOpen className="w-4 h-4" /> {prog.entry}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Contact the institution for programme details</p>
                </div>
              )}

              {institution.degrees_offered && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-3">Degrees & Certifications Offered</h4>
                  <div className="flex flex-wrap gap-2">
                    {institution.degrees_offered.map((degree, idx) => (
                      <span key={idx} className="bg-brand-50 text-brand-700 px-3 py-1 rounded-full text-sm">
                        {degree}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'admissions' && (
            <div className="space-y-6">
              {/* Application Deadline */}
              {institution.application_deadline && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-amber-700 font-semibold mb-1">
                    <Calendar className="w-5 h-5" /> Application Deadline
                  </div>
                  <p className="text-amber-800">{institution.application_deadline}</p>
                </div>
              )}

              {/* Mature Student Entry */}
              {institution.mature_student_entry && (
                <div className="bg-brand-50 border border-brand-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-brand-700 font-semibold mb-1">
                    <RefreshCw className="w-5 h-5" /> Mature Student Entry Available
                  </div>
                  <p className="text-brand-800">
                    Students {institution.mature_entry_age || 21}+ years may apply without traditional entry requirements.
                    Work experience and alternative qualifications considered.
                  </p>
                </div>
              )}

              {/* Flexible Options */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Study Options</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className={`p-3 rounded-lg border ${institution.has_evening_programmes ? 'bg-brand-50 border-brand-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-2">
                      {institution.has_evening_programmes ? (
                        <CheckCircle className="w-5 h-5 text-brand-600" />
                      ) : (
                        <X className="w-5 h-5 text-gray-400" />
                      )}
                      <span className={institution.has_evening_programmes ? 'text-brand-700' : 'text-gray-500'}>Evening Classes</span>
                    </div>
                  </div>
                  <div className={`p-3 rounded-lg border ${institution.has_weekend_programmes ? 'bg-brand-50 border-brand-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-2">
                      {institution.has_weekend_programmes ? (
                        <CheckCircle className="w-5 h-5 text-brand-600" />
                      ) : (
                        <X className="w-5 h-5 text-gray-400" />
                      )}
                      <span className={institution.has_weekend_programmes ? 'text-brand-700' : 'text-gray-500'}>Weekend Classes</span>
                    </div>
                  </div>
                  <div className={`p-3 rounded-lg border ${institution.has_online_programmes ? 'bg-brand-50 border-brand-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-2">
                      {institution.has_online_programmes ? (
                        <CheckCircle className="w-5 h-5 text-brand-600" />
                      ) : (
                        <X className="w-5 h-5 text-gray-400" />
                      )}
                      <span className={institution.has_online_programmes ? 'text-brand-700' : 'text-gray-500'}>Online Learning</span>
                    </div>
                  </div>
                  <div className={`p-3 rounded-lg border ${institution.has_accommodation ? 'bg-brand-50 border-brand-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-2">
                      {institution.has_accommodation ? (
                        <CheckCircle className="w-5 h-5 text-brand-600" />
                      ) : (
                        <X className="w-5 h-5 text-gray-400" />
                      )}
                      <span className={institution.has_accommodation ? 'text-brand-700' : 'text-gray-500'}>On-Campus Housing</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'costs' && (
            <div className="space-y-6">
              {/* Tuition Fees */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Estimated Tuition (per year)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {institution.tuition_range_undergraduate_jmd && (
                    <div className="bg-blue-50 rounded-xl p-4">
                      <div className="text-sm text-blue-600 mb-1">Undergraduate</div>
                      <div className="text-xl font-bold text-blue-800">
                        {formatCurrency(institution.tuition_range_undergraduate_jmd.min)} - {formatCurrency(institution.tuition_range_undergraduate_jmd.max)}
                      </div>
                    </div>
                  )}
                  {institution.tuition_range_graduate_jmd && (
                    <div className="bg-purple-50 rounded-xl p-4">
                      <div className="text-sm text-purple-600 mb-1">Graduate/Postgraduate</div>
                      <div className="text-xl font-bold text-purple-800">
                        {formatCurrency(institution.tuition_range_graduate_jmd.min)} - {formatCurrency(institution.tuition_range_graduate_jmd.max)}
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">* Fees vary by programme. Contact institution for current rates.</p>
              </div>

              {/* Financial Aid Options */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Financial Aid Accepted</h4>
                <div className="space-y-2">
                  {institution.accepts_slb_loans && (
                    <div className="flex items-center gap-3 p-3 bg-brand-50 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-brand-600" />
                      <div>
                        <div className="font-medium text-brand-800">Students' Loan Bureau (SLB)</div>
                        <div className="text-sm text-brand-600">Government student loans available</div>
                      </div>
                    </div>
                  )}
                  {institution.accepts_path && (
                    <div className="flex items-center gap-3 p-3 bg-brand-50 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-brand-600" />
                      <div>
                        <div className="font-medium text-brand-800">PATH Benefits</div>
                        <div className="text-sm text-brand-600">Programme of Advancement Through Health and Education</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== FINANCIAL SUPPORT SECTION ====================
const FinancialSupportSection = ({ data }) => {
  const [expanded, setExpanded] = useState(false);

  // BUGFIX: this read `data.programmes`, but financial_support in
  // tertiary-institutions.json is an object keyed by programme
  // (students_loan_bureau / path / jamvat) and has no `programmes` array at
  // all. `undefined?.map()` short-circuited to nothing, so the toggle flipped
  // state and expanded an empty panel — the section looked like a dead button.
  // Normalise both shapes so it survives either.
  const programmes = Array.isArray(data?.programmes)
    ? data.programmes
    : Object.values(data || {}).filter((v) => v && typeof v === 'object' && v.name);

  if (!programmes.length) return null;

  return (
    <div className="bg-gradient-to-br from-brand-50 to-brand-50 border border-brand-200 rounded-2xl p-6 mb-8">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-brand-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-brand-800">Financial Support Options</h2>
            <p className="text-sm text-brand-600">Loans, grants, and assistance programmes</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-brand-600" /> : <ChevronDown className="w-5 h-5 text-brand-600" />}
      </button>

      {expanded && (
        <div className="mt-6 space-y-4">
          {programmes.map((prog) => (
            <div key={prog.name} className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-bold text-gray-800">{prog.name}</h3>
              <p className="text-sm text-gray-600 mt-1">{prog.description}</p>

              {prog.eligibility && (
                <div className="mt-3 text-sm">
                  <span className="font-medium text-gray-700">Eligibility: </span>
                  <span className="text-gray-600">{prog.eligibility}</span>
                </div>
              )}

              {/* Loan-specific terms. Only the SLB entry carries these, so they
                  render conditionally rather than as a fixed table. */}
              {(prog.max_loan || prog.interest_rate || prog.repayment) && (
                <dl className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  {[
                    ['Covers', prog.max_loan],
                    ['Interest', prog.interest_rate],
                    ['Repayment', prog.repayment]
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} className="bg-gray-50 rounded-lg px-3 py-2">
                      <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</dt>
                      <dd className="text-gray-700 mt-0.5">{value}</dd>
                    </div>
                  ))}
                </dl>
              )}

              {prog.website && (
                <a
                  href={prog.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-3 text-sm text-brand-600 hover:underline font-medium"
                >
                  <Globe className="w-4 h-4" /> Website <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== MAIN TERTIARY EDUCATION HUB ====================
export const TertiaryEducationHub = ({ setPage, goBack }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedParish, setSelectedParish] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedInstitution, setSelectedInstitution] = useState(null);

  const institutions = tertiaryData.data || [];
  const financialSupport = tertiaryData.financial_support;

  const institutionTypes = [
    { id: 'all', label: 'All Institutions' },
    { id: 'university', label: 'Universities' },
    { id: 'teachers_college', label: 'Teachers Colleges' },
    { id: 'community_college', label: 'Community Colleges' },
    { id: 'vocational', label: 'Vocational/TVET' },
    { id: 'specialized', label: 'Specialized' }
  ];

  const parishes = ['all', 'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary', 'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland', 'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'];

  // Filter institutions
  const filteredInstitutions = useMemo(() => {
    return institutions.filter(inst => {
      const matchesSearch = searchQuery === '' ||
        inst.institution_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (inst.short_name && inst.short_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (inst.notable_programmes && inst.notable_programmes.some(p => p.name.toLowerCase().includes(searchQuery.toLowerCase())));

      const matchesType = selectedType === 'all' || inst.institution_type === selectedType;
      const matchesParish = selectedParish === 'all' || inst.parish === selectedParish;

      return matchesSearch && matchesType && matchesParish;
    });
  }, [institutions, searchQuery, selectedType, selectedParish]);

  // Stats
  const stats = useMemo(() => ({
    total: institutions.length,
    universities: institutions.filter(i => i.institution_type === 'university').length,
    colleges: institutions.filter(i => ['teachers_college', 'community_college'].includes(i.institution_type)).length,
    vocational: institutions.filter(i => i.institution_type === 'vocational').length
  }), [institutions]);

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-door-tertiary to-door-tertiary-dark rounded-2xl p-8 text-white mb-8">
        <div className="flex items-center gap-2 text-white/80 text-sm mb-2">
          <GraduationCap className="w-5 h-5" />
          <span>Higher Education Guide</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-black mb-3">
          Tertiary Education in Jamaica
        </h1>
        <p className="text-lg text-white/90 mb-6 max-w-2xl">
          Universities, colleges, and vocational training. Find your path forward - whether straight from high school or returning to education.
        </p>

        {/* Quick Stats */}
        <div className="flex flex-wrap gap-3">
          <span className="bg-white/15 px-4 py-2 rounded-full text-sm">
            {stats.universities} Universities
          </span>
          <span className="bg-white/15 px-4 py-2 rounded-full text-sm">
            {stats.colleges} Colleges
          </span>
          <span className="bg-white/15 px-4 py-2 rounded-full text-sm">
            {stats.vocational} Vocational/TVET
          </span>
        </div>
      </div>

      {/* Career Planning Tools - Top Priority */}
      <div className="mb-8">
        <h3 className="text-xl font-bold text-gray-800 mb-4">Career Planning Tools</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <button
            onClick={() => setPage('career-path')}
            className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-6 text-left text-white shadow-md hover:shadow-lg transition-all"
          >
            <Target className="w-10 h-10 mb-3 opacity-90" />
            <h4 className="font-bold text-lg">Career Path Visualiser</h4>
            <p className="text-purple-100 text-sm mt-1">Discover careers that match your interests and strengths</p>
          </button>
          <button
            onClick={() => setPage('life-path')}
            className="bg-gradient-to-br from-brand-500 to-cyan-600 rounded-xl p-6 text-left text-white shadow-md hover:shadow-lg transition-all"
          >
            <TrendingUp className="w-10 h-10 mb-3 opacity-90" />
            <h4 className="font-bold text-lg">Life Path Simulator</h4>
            <p className="text-cyan-100 text-sm mt-1">Explore different educational journeys and outcomes</p>
          </button>
        </div>
      </div>

      {/* Financial Support Section */}
      <FinancialSupportSection data={financialSupport} />

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name or programme..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Filter Toggle (mobile) */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden flex items-center gap-2 px-4 py-3 bg-gray-100 rounded-lg"
          >
            <Filter className="w-5 h-5" />
            Filters
            {(selectedType !== 'all' || selectedParish !== 'all') && (
              <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
            )}
          </button>

          {/* Desktop Filters */}
          <div className="hidden md:flex gap-3">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
            >
              {institutionTypes.map(type => (
                <option key={type.id} value={type.id}>{type.label}</option>
              ))}
            </select>
            <select
              value={selectedParish}
              onChange={(e) => setSelectedParish(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="all">All Parishes</option>
              {parishes.slice(1).map(parish => (
                <option key={parish} value={parish}>{parish}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Mobile Filters */}
        {showFilters && (
          <div className="md:hidden mt-4 pt-4 border-t border-gray-100 space-y-3">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
            >
              {institutionTypes.map(type => (
                <option key={type.id} value={type.id}>{type.label}</option>
              ))}
            </select>
            <select
              value={selectedParish}
              onChange={(e) => setSelectedParish(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="all">All Parishes</option>
              {parishes.slice(1).map(parish => (
                <option key={parish} value={parish}>{parish}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-gray-600">
          Showing <span className="font-semibold text-gray-800">{filteredInstitutions.length}</span> institution{filteredInstitutions.length !== 1 ? 's' : ''}
        </p>
        {(selectedType !== 'all' || selectedParish !== 'all' || searchQuery) && (
          <button
            onClick={() => { setSelectedType('all'); setSelectedParish('all'); setSearchQuery(''); }}
            className="text-sm text-purple-600 hover:underline flex items-center gap-1"
          >
            <X className="w-4 h-4" /> Clear filters
          </button>
        )}
      </div>

      <DirectoryLink
        setPage={setPage}
        level="Tertiary"
        label="Search universities & colleges"
        count={LEVEL_COUNTS.Tertiary}
        className="mb-8"
      />

      {/* Institution Grid */}
      {filteredInstitutions.length > 0 ? (
        // This grid was the single biggest contributor to /tertiary running to
        // ~13 phone screens. Capped at 6 with the remainder behind a toggle;
        // the filters above still search the full set, so narrowing by type or
        // parish reaches everything without expanding.
        <div className="mb-8">
          <ShowMoreList
            items={filteredInstitutions}
            initial={6}
            noun="institutions"
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            {(institution) => (
              <InstitutionCard
                key={institution.id}
                institution={institution}
                onSelect={setSelectedInstitution}
              />
            )}
          </ShowMoreList>
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-xl mb-8">
          <GraduationCap className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">No Institutions Found</h3>
          <p className="text-gray-500">Try adjusting your search or filters</p>
        </div>
      )}

      {/* Earn & Learn Opportunities */}
      <div className="mb-8">
        <h3 className="text-xl font-bold text-gray-800 mb-4">Earn & Learn Opportunities</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <button
            onClick={() => setPage('side-hustles')}
            className="bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl p-5 text-left text-white shadow-md hover:shadow-lg transition-all"
          >
            <DollarSign className="w-8 h-8 mb-2 opacity-90" />
            <h4 className="font-bold mb-1">Student Side Hustles</h4>
            <p className="text-xs opacity-90">Safe, legal ways to earn while you learn</p>
          </button>
          <button
            onClick={() => setPage('summer-employment')}
            className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl p-5 text-left text-white shadow-md hover:shadow-lg transition-all"
          >
            <Briefcase className="w-8 h-8 mb-2 opacity-90" />
            <h4 className="font-bold mb-1">Summer Employment</h4>
            <p className="text-xs opacity-90">Find summer jobs, internships, and programs</p>
          </button>
          <button
            onClick={() => setPage('opportunity-matcher')}
            className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-5 text-left text-white shadow-md hover:shadow-lg transition-all"
          >
            <Zap className="w-8 h-8 mb-2 opacity-90" />
            <h4 className="font-bold mb-1">Opportunity Matcher</h4>
            <p className="text-xs opacity-90">Match your interests with jobs and internships</p>
          </button>
        </div>
      </div>

      {/* Quick Links */}
      <ResourceLinkGrid
        title="Related Resources"
        setPage={setPage}
        items={[
          { title: 'Scholarships', desc: 'Find funding opportunities', icon: DollarSign, page: 'funding' },
          { title: 'CAPE Guide', desc: 'Prepare for university entry', icon: BookOpen, page: 'exams-cape' },
          { title: 'Education Jobs', desc: 'Teaching positions', icon: Briefcase, page: 'education-jobs' }
        ]}
      />

      {/* Institution Detail Modal */}
      {selectedInstitution && (
        <InstitutionDetail
          institution={selectedInstitution}
          onClose={() => setSelectedInstitution(null)}
        />
      )}
    </div>
  );
};

export default TertiaryEducationHub;
